<template>
  <div>
    <a-modal
      v-model:visible="localVisible"
      :title="form.topic_name || 'ตั้งเบิกจ่ายรถซัพ'"
      :afterClose="handleAfterClose"
      width="1000px"
      :footer="null"
    >
      <a-form :model="form" :key="formKey" layout="vertical" @finish="submitBilling">
        <a-divider orientation="left">บริษัทรถ</a-divider>

        <a-row>
          <a-col>
            <a-form-item
              label="ชื่อบริษัทรถ"
              name="sub_car_company_id"
              :rules="[{ required: true, message: 'โปรดเลือกบริษัทรถ' }]"
            >
              <a-select
                v-model:value="form.sub_car_company_id"
                @change="auto_fill_sub_car_company"
                placeholder="บริษัทรถ"
              >
                <a-select-option
                  v-for="{ id, fullname } in car_company_list"
                  :key="id"
                  :value="id"
                >
                  {{ fullname }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row>
          <a-col>
            <a-form-item
              label="ชื่อ-นามสกุลผู้ขับ"
              name="driver_id"
              :rules="[{ required: true, message: 'เลือกผู้ขับ' }]"
            >
              <a-select
                v-model:value="form.driver_id"
                @change="auto_fill_driver_name"
                placeholder="เลือกผู้ขับ"
              >
                <a-select-option
                  v-for="{ id, firstname, lastname } in driver_list"
                  :key="id"
                  :value="id"
                >
                  {{ firstname }} {{ lastname }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row v-if="form.driver_id">
          <a-col>
            <a-form-item label="ทะเบียนรถ">
              <a-input v-model:value="form.plate_no" disabled />
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item label="วันที่ทำจ่าย (เริ่ม - สิ้นสุด)">
          <a-range-picker v-model:value="form.dateRange" format="DD-MM-YYYY" />
        </a-form-item>

        <a-form-item v-if="form.release_date" label="Release Date">
          <a-input v-model:value="form.release_date" disabled />
        </a-form-item>

        <a-row justify="end">
          <a-button type="primary" html-type="submit">บันทึก</a-button>
        </a-row>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import CarCompanyService from '../../api/CarCompanyService';
import CarService from '../../api/CarService.js';
import SystemService from '../../api/SystemService';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import { message } from 'ant-design-vue';
import Swal from 'sweetalert2';
import { exportBillingExcel } from "../../api/exportBillingExcel";

dayjs.extend(buddhistEra);

export default {
  name: 'SupportVehicleBillingModal',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    afterClose: {
      type: Function,
      default: () => {}
    },
    releaseDate: {
      type: [String, Date],
      default: null
    }
  },
  data() {
    return {
      localVisible: false,
      car_company_list: [],
      driver_list: [],
      formKey: 0,
      defaultForm: {
        topic_name: 'ตั้งเบิกจ่ายรถซัพ',
        sub_car_company_id: null,
        sub_car_name: '',
        driver_id: null,
        driver_name: '',
        plate_no: '',
        dateRange: [],
        release_date: null
      },
      form: {}
    };
  },
  watch: {
    visible(val) {
      this.localVisible = val;
      if (val) {
        this.init_options();
      }
    }
  },
  methods: {
    handleAfterClose() {
      this.form = { ...this.defaultForm };
      this.formKey++;
      this.$emit('update:visible', false);
      this.afterClose();
    },
    closeModal() {
      this.localVisible = false;
    },
    submitBilling() {
      const driver = this.driver_list.find(d => d.id === this.form.driver_id);
      const fullName = `${driver.firstname} ${driver.lastname}`;
      const plateNo = this.form.plate_no;
      const dateRange = this.form.dateRange;

      const expenses = [
        { desc: "", amount: '' },
      ];

      exportBillingExcel({
        fullName,
        plateNo,
        dateRange,
        expenses,
      });

      this.closeModal();
    },
    auto_fill_sub_car_company() {
      const company = this.car_company_list.find(c => c.id === this.form.sub_car_company_id);
      this.form.sub_car_name = company ? company.fullname : '';
    },
    async auto_fill_driver_name() {
      const driver = this.driver_list.find(d => d.id === this.form.driver_id);
      if (driver) {
        this.form.driver_name = `${driver.firstname || ''} ${driver.lastname || ''}`.trim();

        try {
          const carData = await CarService.getCarByDriver(driver.id);
          if (carData && carData.plate_no) {
            this.form.plate_no = carData.plate_no;
          } else if (Array.isArray(carData) && carData.length > 0) {
            this.form.plate_no = carData[0].plate_no || '';
          } else {
            this.form.plate_no = '';
          }
        } catch (error) {
          console.error('ดึงทะเบียนรถล้มเหลว', error);
          this.form.plate_no = '';
        }
      } else {
        this.form.driver_name = '';
        this.form.plate_no = '';
      }
    },
    async init_options() {
      try {
        this.form = { ...this.defaultForm };
        this.form.release_date = this.releaseDate || null;

        this.driver_list = await SystemService.get_all('Driver');
        this.car_company_list = await CarCompanyService.get_car_companies();
        message.success({ content: 'โหลดข้อมูลเรียบร้อย', key: 'init_options', duration: 2 });
      } catch (err) {
        message.error('โหลดข้อมูลล้มเหลว');
        console.error(err);
      }
    }
  }
};
</script>

<style scoped>
p {
  margin: 0;
  color: black !important;
}
h1 {
  font-size: 26px;
  margin: 5px 0;
}
h2 {
  font-size: 18px;
  margin: 5px 0;
}
.ant-row {
  margin-bottom: 8px;
}
.ant-col-12 {
  min-width: 300px;
  padding: 4px;
}
.ant-select,
.ant-picker {
  width: 100%;
}
.ant-col {
  width: 100%;
}
.ant-form-item {
  margin-bottom: 0;
}
</style>
